import React, { useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import matter from 'gray-matter';
import { Components } from 'react-markdown';
import { useTheme } from '../contexts/ThemeContext';

// Type definition for unified pluggable list
type PluggableList = Array<any>;

interface MarkdownRendererProps {
  content: string;
  className?: string;
  components?: Components;
  onMetadata?: (data: Record<string, any>) => void;
  remarkPlugins?: PluggableList;
}

const joinClassNames = (...values: Array<string | undefined>) => values.filter(Boolean).join(' ');

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className,
  components,
  onMetadata,
  remarkPlugins,
}) => {
  const { theme } = useTheme();

  const parsed = useMemo(() => {
    try {
      return matter(content ?? '');
    } catch (error) {
      console.error('Error parsing markdown front matter:', error);
      return { data: {}, content: content ?? '' };
    }
  }, [content]);

  useEffect(() => {
    if (onMetadata) {
      onMetadata(parsed.data as Record<string, any>);
    }
  }, [parsed.data, onMetadata]);

  const markdownComponents = useMemo<Components>(() => {
    const textPrimary = theme.colors.text.primary;
    const textSecondary = theme.colors.text.secondary;
    const borderPrimary = theme.colors.border.primary;

    return {
      h1: ({ children }) => (
        <h1 className={joinClassNames('text-lg font-semibold mb-3 mt-5 first:mt-0', textPrimary)}>
          {children}
        </h1>
      ),
      h2: ({ children }) => (
        <h2 className={joinClassNames('text-base font-semibold mb-2 mt-4 first:mt-0', textPrimary)}>
          {children}
        </h2>
      ),
      h3: ({ children }) => (
        <h3
          className={joinClassNames('text-[13px] font-semibold mb-2 mt-3 first:mt-0', textPrimary)}
        >
          {children}
        </h3>
      ),
      p: ({ children }) => (
        <p className={joinClassNames('mb-3.5 leading-7 text-[14.5px] first:mt-0', textPrimary)}>
          {children}
        </p>
      ),
      ul: ({ children }) => (
        <ul
          className={joinClassNames(
            'mb-3.5 space-y-1.5 pl-5 list-disc marker:text-nb-text-muted first:mt-0',
            textPrimary
          )}
        >
          {children}
        </ul>
      ),
      ol: ({ children }) => (
        <ol
          className={joinClassNames(
            'mb-3.5 space-y-1.5 pl-5 list-decimal marker:text-nb-text-muted first:mt-0',
            textPrimary
          )}
        >
          {children}
        </ol>
      ),
      li: ({ children }) => (
        <li className={joinClassNames('leading-[1.625] text-[14.5px]', textPrimary)}>{children}</li>
      ),
      strong: ({ children }) => (
        <strong className={joinClassNames('font-semibold', textPrimary)}>{children}</strong>
      ),
      blockquote: ({ children }) => (
        <blockquote
          className={joinClassNames(
            'border-l-4 pl-4 italic text-sm first:mt-0',
            borderPrimary,
            textSecondary
          )}
        >
          {children}
        </blockquote>
      ),
      code: ({ inline, children }) =>
        inline ? (
          <code className="px-1 py-0.5 rounded bg-black/20 text-xs">{children}</code>
        ) : (
          <pre
            className={joinClassNames(
              'p-4 rounded-lg overflow-x-auto text-sm',
              theme.colors.bg.secondary,
              textPrimary
            )}
          >
            <code>{children}</code>
          </pre>
        ),
      table: ({ children }) => (
        <div className="overflow-x-auto first:mt-0">
          <table className={joinClassNames('w-full border-collapse text-sm', borderPrimary)}>
            {children}
          </table>
        </div>
      ),
      thead: ({ children }) => (
        <thead className={joinClassNames(theme.colors.bg.secondary, textPrimary)}>{children}</thead>
      ),
      tbody: ({ children }) => <tbody className={textPrimary}>{children}</tbody>,
      tr: ({ children }) => (
        <tr className={joinClassNames('border-b last:border-b-0', borderPrimary)}>{children}</tr>
      ),
      th: ({ children }) => (
        <th className={joinClassNames('text-left font-semibold px-3 py-2', textPrimary)}>
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td className={joinClassNames('px-3 py-2 align-top', textPrimary)}>{children}</td>
      ),
      a: ({ children, href }) => (
        <a href={href} target="_blank" rel="noreferrer" className="text-nb-accent hover:underline">
          {children}
        </a>
      ),
      ...components,
    };
  }, [theme.colors, components]);

  const plugins = useMemo(() => {
    const base: PluggableList = [remarkGfm];
    if (remarkPlugins && remarkPlugins.length > 0) {
      base.push(...remarkPlugins);
    }
    return base;
  }, [remarkPlugins]);

  return (
    <div className={joinClassNames('markdown-renderer', className)}>
      <ReactMarkdown remarkPlugins={plugins} components={markdownComponents}>
        {parsed.content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
